import type { ColDef } from "ag-grid-community";
import { makeAutoObservable } from "mobx";
import type { DictionaryItem } from "@synthesis/shared-services/internal/dictionaries/types";
import { makeStoreProvider } from "@synthesis/shared-utils";

export type ReferenceDocumentType = {
  id: string;
  name: string;
  description: string;
  uses: string;
};

// Бэкенда нет — типы документов захардкожены по макету «Справочники - Типы документов».
export const MOCK_REFERENCE_DOCUMENT_TYPES: ReferenceDocumentType[] = [
  {
    id: "constituent",
    name: "Заверенные копии учредительных документов",
    description: "Устав и иные учредительные документы организации-заявителя, заверенные в установленном порядке.",
    uses: "1 комплект",
  },
  {
    id: "tax-registration",
    name: "Свидетельство / документ о постановке на учёт в налоговом органе",
    description: "Подтверждает постановку заявителя на налоговый учёт (ИНН/КПП).",
    uses: "1 комплект",
  },
  {
    id: "egrul",
    name: "Документ, подтверждающий внесение записи в ЕГРЮЛ",
    description: "Выписка или лист записи ЕГРЮЛ о государственной регистрации юридического лица.",
    uses: "1 комплект",
  },
  {
    id: "sp-registration",
    name: "Заверенная копия свидетельства о государственной регистрации ИП",
    description: "Требуется, если заявитель — индивидуальный предприниматель.",
    uses: "1 комплект",
  },
  {
    id: "land-rights",
    name: "Правоустанавливающие документы на земельные участки",
    description: "Документы на участки, где планируется разместить новый или развить существующий путь необщего пользования.",
    uses: "1 комплект",
  },
  {
    id: "topographic",
    name: "Топографический план местности",
    description: "План с предполагаемым местом строительства нового пути относительно существующей инфраструктуры РЖД.",
    uses: "1 комплект",
  },
  {
    id: "cadastral",
    name: "Кадастровые номера земельных участков",
    description: "Отображение кадастровых номеров, а при их отсутствии — кадастровых кварталов.",
    uses: "1 комплект",
  },
  {
    id: "station-plan",
    name: "Схематический план станции с местом примыкания",
    description: "Предполагаемое место примыкания к инфраструктуре РЖД или к пути другого владельца.",
    uses: "1 комплект",
  },
  {
    id: "owner-consent",
    name: "Письменное согласие владельца пути необщего пользования",
    description: "Согласие владельца пути, к которому планируется примыкание.",
    uses: "1 комплект",
  },
  {
    id: "track-plan",
    name: "Схематический план существующего путевого развития",
    description: "План путевого развития пути необщего пользования заявителя.",
    uses: "1 комплект",
  },
  {
    id: "cargo-volume",
    name: "Информация об объёмах перевозок",
    description: "Существующие и планируемые объёмы перевозок, корреспонденции и направления перевозок.",
    uses: "1 комплект",
  },
  {
    id: "correspondence",
    name: "Сведения по каждой корреспонденции",
    description: "Грузопоток, годовой объём, грузоотправитель/получатель, вид сообщения, этапность, тип подвижного состава, вид отправки.",
    uses: "2 комплекта",
  },
  {
    id: "guarantee-letters",
    name: "Заверенные копии гарантийных писем от грузоотправителей",
    description: "Подтверждение планируемых объёмов перевозок со стороны грузоотправителей.",
    uses: "1 комплект",
  },
  {
    id: "project-solutions",
    name: "Проектные решения или предпроектные проработки",
    description: "Основные проектные решения перспективного развития инфраструктуры РЖД и пути заявителя.",
    uses: "2 комплекта",
  },
];

export type DocumentTypeFormValues = {
  name: string;
  description?: string;
};

// ---------------------------------- ВИДЫ РАБОТ (справочник) ----------------------------------

/** Элемент справочника для отображения в ЛК: код и наименование. */
export type ReferenceDictionaryItem = {
  code: string;
  displayName: string;
};

/** Маппинг элементов справочника «Виды работ» из DictionaryItem в строки ЛК. */
export const buildReferenceWorkTypes = (items: DictionaryItem[] | null | undefined): ReferenceDictionaryItem[] =>
  (items ?? [])
    .map((item) => ({ code: item.code?.trim() ?? "", displayName: item.displayName?.trim() ?? "" }))
    .filter((item) => item.code !== "" && item.displayName !== "");

// ---------------------------------- COLUMNS ----------------------------------

export const DOCUMENT_TYPE_COLUMNS: ColDef<ReferenceDocumentType>[] = [
  { colId: "name", headerName: "Наименование", field: "name", width: 588, minWidth: 360 },
  { colId: "description", headerName: "Описание", field: "description", minWidth: 360, flex: 1 },
  { colId: "uses", headerName: "Используется", field: "uses", width: 130 },
];

export const WORK_TYPES_COLUMNS: ColDef<ReferenceDictionaryItem>[] = [
  { colId: "displayName", headerName: "Наименование", field: "displayName", minWidth: 360, flex: 1 },
  { colId: "code", headerName: "Код", field: "code", width: 220 },
];

// ---------------------------------- STORE ----------------------------------

export type WorkTypeFormValues = {
  name: string;
  code?: string;
};

export type ReferencePopupAction =
  | { type: "add-document-type" }
  | { type: "edit-document-type"; documentType: ReferenceDocumentType }
  | { type: "add-work-type" }
  | { type: "edit-work-type"; workType: ReferenceDictionaryItem };

export const DOCUMENT_TYPES_TAB_KEY = "document-types";
export const WORK_TYPES_TAB_KEY = "work-types";

class PageStore {
  public popupAction: ReferencePopupAction | null = null;
  public activeTab = DOCUMENT_TYPES_TAB_KEY;

  constructor() {
    makeAutoObservable(this);
  }

  public setPopupAction(action: ReferencePopupAction | null) {
    this.popupAction = action;
  }

  public setActiveTab(tab: string) {
    this.activeTab = tab;
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
